import React, { useId, useState } from "react";
import { Icons, IconName } from "../../../icons";
import "./inputs.scss";

export type InputSize = "sm" | "md" | "lg";

export interface InputProps
	extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
	label?: string;
	hint?: string;
	errorMessage?: string;
	invalid?: boolean;
	size?: InputSize;
	iconName?: IconName;
	iconPosition?: "left" | "right";
	loading?: boolean;
	showPasswordToggle?: boolean;
	fullWidth?: boolean;
	wrapperClassName?: string;
}

export const Input: React.FC<InputProps> = ({
	label,
	hint,
	errorMessage,
	invalid = false,
	size = "md",
	iconName,
	iconPosition = "left",
	loading = false,
	showPasswordToggle = false,
	fullWidth = false,
	wrapperClassName = "",
	className = "",
	id,
	disabled,
	type = "text",
	...rest
}) => {
	const generatedId = useId();
	const inputId = id ?? generatedId;
	const [passwordVisible, setPasswordVisible] = useState(false);

	const isInvalid = invalid || Boolean(errorMessage);
	const isPassword = type === "password";
	const resolvedType =
		isPassword && showPasswordToggle && passwordVisible ? "text" : type;

	const LeftIcon = iconName && iconPosition === "left" ? Icons[iconName] : null;
	const RightIcon =
		iconName && iconPosition === "right" && !loading && !showPasswordToggle
			? Icons[iconName]
			: null;
	const SpinnerIcon = loading ? Icons.spinner : null;
	const ToggleIcon = passwordVisible ? Icons.eyeOff : Icons.eye;

	const controlClasses = [
		"ds-input__control",
		`ds-input__control--${size}`,
		isInvalid ? "ds-input__control--invalid" : "",
		disabled ? "ds-input__control--disabled" : "",
	]
		.filter(Boolean)
		.join(" ");

	return (
		<div
			className={[
				"ds-input",
				fullWidth ? "ds-input--full" : "",
				wrapperClassName,
			]
				.filter(Boolean)
				.join(" ")}
		>
			{label ? (
				<label htmlFor={inputId} className="ds-input__label">
					{label}
				</label>
			) : null}

			<div className={controlClasses}>
				{LeftIcon ? (
					<span className="ds-input__icon ds-input__icon--left" aria-hidden>
						<LeftIcon />
					</span>
				) : null}

				<input
					{...rest}
					id={inputId}
					type={resolvedType}
					disabled={disabled}
					className={["ds-input-field", className].filter(Boolean).join(" ")}
					aria-invalid={isInvalid || undefined}
					aria-describedby={
						errorMessage
							? `${inputId}-error`
							: hint
								? `${inputId}-hint`
								: undefined
					}
				/>

				{SpinnerIcon ? (
					<span
						className="ds-input__icon ds-input__icon--right ds-input__icon--spin"
						aria-hidden
					>
						<SpinnerIcon />
					</span>
				) : null}

				{RightIcon ? (
					<span className="ds-input__icon ds-input__icon--right" aria-hidden>
						<RightIcon />
					</span>
				) : null}

				{isPassword && showPasswordToggle ? (
					<button
						type="button"
						className="ds-input__toggle"
						onClick={() => setPasswordVisible((v) => !v)}
						disabled={disabled}
						aria-label={passwordVisible ? "Ocultar contraseña" : "Mostrar contraseña"}
					>
						<ToggleIcon />
					</button>
				) : null}
			</div>

			{errorMessage ? (
				<p id={`${inputId}-error`} className="ds-input__error" role="alert">
					{errorMessage}
				</p>
			) : hint ? (
				<p id={`${inputId}-hint`} className="ds-input__hint">
					{hint}
				</p>
			) : null}
		</div>
	);
};
